import moment from 'moment';
import fmt from 'utils/fmt';
import { readSettingFile, writeSettingFile } from 'shared/Settings';
import iconv from 'iconv-lite';

/**
 * ESC/POS 제어 명령어 (ESC/POS Control Codes)
 */
const ESC_ALIGN_LEFT = '\x1b\x61\x00';
const ESC_ALIGN_CENTER = '\x1b\x61\x01';
const ESC_BOLD_ON = '\x1b\x45\x01';
const ESC_BOLD_OFF = '\x1b\x45\x00';
const ESC_DOUBLE_SIZE = '\x1b\x21\x30';
const ESC_NORMAL_SIZE = '\x1b\x21\x02';

/**
 * iconv-lite를 이용한 EUC-KR(CP949) 바이트 배열 변환 유틸리티
 */
const encodeToEucKrBytes = (str) => {
  const buf = iconv.encode(str, 'euc-kr');
  return Uint8Array.from(buf);
};

/**
 * 1. Web Serial API를 활용한 시리얼(COM) 포트 인쇄
 */
export const printToSerial = async (selectedPort, selectedBaudRate = 9600, content = '') => {
  if (!('serial' in navigator)) {
    alert('현재 브라우저는 Web Serial API를 지원하지 않습니다. Chrome 또는 Edge 브라우저를 사용해 주세요.');
    return;
  }

  
try {
    let port;
    
    // 1. 이미 승인/페어링된 포트 목록 확인 (팝업 없음)
    const existingPorts = await navigator.serial.getPorts();

    if (existingPorts.length > 0) {
      port = existingPorts[0]; // 승인된 기존 포트 사용
    } else {
      // 2. 승인된 포트가 없으면 최초 1회만 사용자 선택 팝업 표시
      port = await navigator.serial.requestPort();
    }

    // 포트가 열려있지 않은 상태일 때만 open() 실행
    if (!port.readable) {
      await port.open({ baudRate: Number(selectedBaudRate) || 9600 });
    }

    // 1) 한글 모드 ON (HEX: 1c 26)
    const hangulOnBytes = new Uint8Array([0x1c, 0x26]);

    // 2) 텍스트 인코딩
    const contentBytes = encodeToEucKrBytes(content);

    // 3) 5줄 피드 + GS V 66 컷팅 명령어 (HEX: 1b 64 05 1d 56 42 00)
    const settings = readSettingFile() || {};
    const cutType = settings.cutType || 'GS_V_66'; // 기본값 설정

    let cutCmd = [0x1b, 0x64, 0x05, 0x1d, 0x56, 0x42, 0x00]; // 기본: GS V 66 (현재 잘되는 방식)

    if (cutType === 'ESC_I') {
      cutCmd = [0x1b, 0x64, 0x05, 0x1b, 0x69]; // ESC i
    } else if (cutType === 'ESC_M') {
      cutCmd = [0x1b, 0x64, 0x05, 0x1b, 0x6d]; // ESC m
    } else if (cutType === 'GS_V_0') {
      cutCmd = [0x1b, 0x64, 0x05, 0x1d, 0x56, 0x00]; // GS V 0
    }

    const cutBytes = new Uint8Array(cutCmd);

    // 4) 데이터 바이너리 병합
    const finalData = new Uint8Array(hangulOnBytes.length + contentBytes.length + cutBytes.length);
    finalData.set(hangulOnBytes, 0);
    finalData.set(contentBytes, hangulOnBytes.length);
    finalData.set(cutBytes, hangulOnBytes.length + contentBytes.length);

    const writer = port.writable.getWriter();
    await writer.write(finalData);

    // 데이터 송신 후 프린터가 컷팅 작업을 완료할 수 있도록 300ms 대기
    await new Promise((resolve) => setTimeout(resolve, 300));

    writer.releaseLock();
    await port.close();

    console.log(`[Web Serial] ${selectedPort || '포트'} 전송 및 커팅 완료`);
  } catch (error) {
    if (error.name === 'NotFoundError') {
      console.log('포트 선택이 취소되었습니다.');
    } else {
      console.error('Web Serial API 인쇄 실패:', error);
      alert(`시리얼 인쇄 실패: ${error.message}`);
    }
  }
};

/**
 * 2. 브라우저 인쇄 (COM 포트가 아닐 경우 실행)
 */
export const printToSystemPrinter = (printerName, content) => {
  try {
    const plainText = content
      .replace(/\x1c\x26/g, '')
      .replace(/\x1b\x61[\x00-\x02]/g, '')
      .replace(/\x1b\x21[\x00-\xff]/g, '')
      .replace(/\x1b\x45[\x00-\x01]/g, '')
      .replace(/\x0a/g, '\n');

    const printWindow = window.open('', '_blank', 'width=400,height=600');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>영수증 인쇄</title>
            <style>
              body {
                font-family: monospace, courier, monospace;
                font-size: 12px;
                white-space: pre-wrap;
                margin: 10px;
              }
            </style>
          </head>
          <body>${plainText.replace(/\n/g, '<br/>')}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    } else {
      window.print();
    }
  } catch (error) {
    console.error('printToSystemPrinter 오류 발생:', error);
  }
};

/**
 * 3. 영수증 텍스트 생성
 */
export const getReciptStr = (orderItem) => {
  const settings = readSettingFile() || {};  
  const orderAt = moment(orderItem.ordrAt).format('YYYY-MM-DD HH:mm:ss');
  const MENU_PAD = 20;
  const PRICE_PAD = 10;

  let s = '';
  s += ESC_NORMAL_SIZE + `주문일시 ${orderAt}     [매장용] \n`;
  s += ESC_BOLD_ON + '결제여부 [결제완료]' + ESC_BOLD_OFF + '\n';
  s += '------------------------------------------\n';
  s += ESC_DOUBLE_SIZE + '접수번호 : ' + orderItem.acceptNum + ' (매장)\n\n';
  s += '테이블 # ' + orderItem.tblNm + ESC_NORMAL_SIZE + '\n';
  s += '------------------------------------------\n';
  s += '메뉴'.padEnd(MENU_PAD) + '수량' + '금액'.padStart(PRICE_PAD) + '\n';
  s += '------------------------------------------\n';

  if (orderItem.ordrItems) {
    for (let i = 0; i < orderItem.ordrItems.length; i++) {
      const value = orderItem.ordrItems[i];

      let itemName = `${value.ordrItemNm}`;
      if (value.menuPriceNm && value.menuPriceNm.length > 0) {
        itemName += ` (${value.menuPriceNm})`;
      }
      const itemQty = String(value.ordrItemQty);

      let menuPad = MENU_PAD - (fmt?.getByte ? fmt.getByte(itemName) : itemName.length) + itemName.length + 3;
      let pricePad = PRICE_PAD + 4;

      const formattedPrice = fmt?.getCurrency ? fmt.getCurrency(value.menuPrice * value.ordrItemQty) : value.menuPrice * value.ordrItemQty;

      s += ESC_BOLD_ON;
      s += `${itemName.padEnd(menuPad)}${itemQty.padStart(2)}${String(formattedPrice).padStart(pricePad)}\n`;
      s += ESC_BOLD_OFF;

      if (value.ordrOptGrps) {
        for (let j = 0; j < value.ordrOptGrps.length; j++) {
          const ordrOptItems = value.ordrOptGrps[j];

          for (let k = 0; k < ordrOptItems.ordrOptItems.length; k++) {
            const item = ordrOptItems.ordrOptItems[k];
            const optionQty = String(item.ordrOptQty);

            const itemNameStr = ' - ' + item.ordrOptNm;
            let menuPad = MENU_PAD - (fmt?.getByte ? fmt.getByte(itemNameStr) : itemNameStr.length) + itemNameStr.length + 3;
            const itemPrice = fmt?.getCurrency ? fmt.getCurrency(item.ordrOptPrice).padStart(pricePad) : String(item.ordrOptPrice).padStart(pricePad);

            s += ESC_BOLD_ON + `${itemNameStr.padEnd(menuPad)}${optionQty.padStart(2)}${itemPrice}\n` + ESC_BOLD_OFF;
          }
        }
      }
    }
  }

  const totalPayAmt = fmt?.getCurrency ? fmt.getCurrency(orderItem.payAmt) : orderItem.payAmt;

  s += ESC_NORMAL_SIZE + '------------------------------------------\n';
  s += ESC_BOLD_ON + `${'주문합계'}${String(totalPayAmt).padStart(PRICE_PAD + 20)} 원` + ESC_NORMAL_SIZE + '\n';
  s += '==========================================\n';

  return s;
};

/**
 * 4. 통합 주문 내역 인쇄
 */
export const printOrderDetail = async (orderItem, payListForOrdr) => {
  const settings = readSettingFile() || {};
  const portIndex = settings.portIndex ?? 0; // 저장해둔 포트 인덱스 (기본값 0)
  const selectedBaudRate = settings.baudRate || 9600;
  const s = getReciptStr(orderItem);

  // 브라우저에서 승인된 시리얼 포트 목록 가져오기
  let targetPort = null;
  if ('serial' in navigator) {
    try {
      const ports = await navigator.serial.getPorts();
      targetPort = ports[portIndex] || ports[0]; // 인덱스에 해당하는 포트 추출
    } catch (err) {
      console.error("시리얼 포트 목록 로드 실패:", err);
    }
  }

  // targetPort 객체가 존재하면 시리얼 인쇄 실행
  if (targetPort) {
    console.log(`[인쇄 방식] 시리얼(COM) 포트 인쇄 실행`, targetPort);
    printToSerial(targetPort, selectedBaudRate, s);
  } else {
    console.log(`[인쇄 방식] OS 시스템 프린터 인쇄 실행 (유효한 시리얼 포트 없음)`);
    printToSystemPrinter(null, s);
  }
};

/**
 * 5. 통합 테스트 인쇄
 */
export const printTest = (selectedPort, selectedBaudRate) => {
  let s = '';
  s += ESC_NORMAL_SIZE + '주문일시 ' + moment().format('YYYY-MM-DD HH:mm:ss') + '     [매장용] \n';
  s += ESC_BOLD_ON + '결제여부 [결제완료]' + ESC_BOLD_OFF + '\n';
  s += '------------------------------------------\n';
  s += ESC_DOUBLE_SIZE + '접수번호 : 123 (매장)\n\n';
  s += '테이블# 3' + ESC_NORMAL_SIZE + '\n';
  s += '------------------------------------------\n';
  s += '메뉴                        수량      금액\n';
  s += '------------------------------------------\n';
  s += ESC_BOLD_ON + '0123456789012345678901234567   1 9,999,999\n' + ESC_BOLD_OFF;
  s += ' + 0123456789012345678901234     9,999,999\n';
  s += '------------------------------------------\n';
  s += ESC_BOLD_ON + '주문합계                      9,999,999 원' + ESC_NORMAL_SIZE + '\n';
  s += '==========================================\n';

  //const isComPort = typeof selectedPort === 'string' && /^COM\d+$/i.test(selectedPort.trim());
  const isComPort = 
  (selectedPort && typeof selectedPort === 'object') || 
  (typeof selectedPort === 'string' && /^COM\d+$/i.test(selectedPort.trim()));

  console.log("isComPort",isComPort);
  if (isComPort) {
    printToSerial(selectedPort, selectedBaudRate, s);
  } else {
    printToSystemPrinter(selectedPort, s);
  }
};

export const convertEscapeStrToHtml = (str) => {
  // if (!str) return '';
  // let returnVal = str.replace(/\n/g, '<br/>');
  // returnVal = returnVal.replace(/ /g, '&nbsp;');
  // return `<code className="w-full">${returnVal}</code>`;
  if (!str) return '';

  // 1. ESC/POS 제어 문자 제거 (ASCII 0x00~0x1F 대역 및 ESC/GS 제어 시퀀스)
  let cleanStr = str
    .replace(/\x1c\x26/g, '')             // 한글 모드 ON
    .replace(/\x1b\x61[\x00-\x02]/g, '') // 정렬 (왼쪽, 중앙, 오른쪽)
    .replace(/\x1b\x21[\x00-\xff]/g, '') // 폰트 크기
    .replace(/\x1b\x45[\x00-\x01]/g, '') // BOLD ON/OFF
    .replace(/\x1b[\x00-\xff]/g, '')     // 기타 남은 ESC 명령어 일괄 제거
    .replace(/[\x00-\x09\x0b-\x1f]/g, '');// 줄바꿈(\n, 0x0A)을 제외한 나머지 제어 문자 제거

  // 2. HTML 줄바꿈 및 공백 변환
  let returnVal = cleanStr.replace(/\n/g, '<br/>');
  returnVal = returnVal.replace(/ /g, '&nbsp;');

  return `<code className="w-full">${returnVal}</code>`;
};

export const getSystemPrinters = async () => [];

export const getAvailableComPorts = async () => {
  if ('serial' in navigator) {
    return await navigator.serial.getPorts();
  }
  return [];
};